import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@/hooks/useTheme';

export default function Services() {
  const { colors } = useTheme();
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(entry.target); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: 'ri-flashlight-fill',
      title: '30-Min Delivery',
      desc: 'Get your fashion delivered within 30 minutes anywhere in Dehradun — fastest in the city.',
    },
    {
      icon: 'ri-ruler-line',
      title: 'Smart Size Exchange',
      desc: 'Wrong size? Delivery partner replaces instantly at your door with all available sizes.',
    },
    {
      icon: 'ri-money-rupee-circle-fill',
      title: 'Cash on Delivery',
      desc: 'Pay only when you receive. No upfront payment. 100% secure COD for every order.',
    },
    {
      icon: 'ri-refresh-line',
      title: '7-Day Easy Returns',
      desc: 'Not satisfied? Return any product within 7 days — no questions asked, full refund.',
    },
    {
      icon: 'ri-verified-badge-fill',
      title: 'Quality Guaranteed',
      desc: 'Every product quality-checked before dispatch. Premium fashion at affordable price.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '24/7 Support',
      desc: 'Our support team is available round the clock to resolve any issue or query.',
    },
  ];

  const deliveryAreas = ['Rajpur Road', 'ISBT', 'Ballupur', 'Clement Town', 'Mussoorie Road', 'Sahastradhara', 'Raipur', 'Prem Nagar', 'Doon University Area'];

  return (
    <section id="services" ref={sectionRef} className="relative py-16 md:py-24 bg-gray-50 dark:bg-slate-800 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-slate-700 to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-slate-700 to-transparent"></div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div 
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4"
              style={{ backgroundColor: `${colors.primary}15`, borderColor: `${colors.primary}30`, border: '1px solid' }}
            >
              <span 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: colors.primary }}
              ></span>
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: colors.primary }}>Why Choose Us</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 dark:text-white mb-4">
              Why <span 
                className="text-transparent bg-clip-text"
                style={{ backgroundImage: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
              >Clothy Bazar?</span>
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-base max-w-xl mx-auto">More than just clothes — experience the future of fashion shopping with our premium services.</p>
          </div>

          {/* Services Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {services.map((service, index) => (
              <div
                key={service.title}
                className={`group bg-white dark:bg-slate-900 rounded-2xl p-6 border hover:-translate-y-1.5 hover:border-gray-300 dark:hover:border-slate-600 transition-all duration-300 dark:border-slate-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ 
                  transitionDelay: `${index * 80}ms`,
                  backgroundColor: `${colors.primary}08`,
                  borderColor: `${colors.primary}20`
                }}
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                  style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
                >
                  <i className={`${service.icon} text-white text-xl`}></i>
                </div>
                <h3 className="text-gray-900 dark:text-white font-black text-base mb-2">{service.title}</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>

          {/* Delivery Areas Banner */}
          <div className={`rounded-3xl overflow-hidden transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="relative p-8 md:p-12" style={{ background: 'linear-gradient(135deg, #1c1917, #292524)' }}>
              <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1.5px, transparent 1.5px)', backgroundSize: '28px 28px' }}></div>
              <div className="relative z-10">
                <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8">
                  <div className="lg:w-2/5">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 mb-4">
                      <i className="ri-map-pin-fill text-orange-400 text-xs"></i>
                      <span className="text-white/70 text-xs font-bold uppercase tracking-wider">Delivery Areas</span>
                    </div>
                    <h3 className="text-white font-black text-2xl md:text-3xl mb-3 leading-tight">
                      Serving All of <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">Dehradun</span>
                    </h3>
                    <p className="text-white/60 text-sm leading-relaxed mb-5">
                      We deliver to all major areas in Dehradun within 30 minutes. Check if we serve your location!
                    </p>
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/8 border border-white/10">
                      <i className="ri-flashlight-fill text-yellow-400 text-xl"></i>
                      <div>
                        <p className="text-white font-bold text-sm">Lightning Fast</p>
                        <p className="text-white/50 text-xs">Average delivery: 28 minutes</p>
                      </div>
                    </div>
                  </div>
                  <div className="lg:w-3/5 w-full">
                    <div className="flex flex-wrap gap-2">
                      {deliveryAreas.map((area) => (
                        <div key={area} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/8 border border-white/10 hover:bg-white/15 transition-all">
                          <i className="ri-map-pin-fill text-orange-400 text-xs"></i>
                          <span className="text-white/80 text-sm font-medium">{area}</span>
                        </div>
                      ))}
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-orange-500/20 border border-orange-500/30">
                        <i className="ri-add-line text-orange-400 text-xs"></i>
                        <span className="text-orange-300 text-sm font-medium">+ More areas</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
